import { useCallback, useEffect, useMemo, useState } from 'react'
import { TIPOS_EM_DESTAQUE } from './visualizacao3d/tabletop/contextoEquipamentos'
import { perfilVeiculoPorId } from './dominio/didatico/perfilVeiculo'
import { PainelAncoragens } from './apresentacao/tabletop/PainelAncoragens'
import { ProvedorSimulacoes, useSimulacoesTabletop } from './apresentacao/tabletop/simulacoes'
import { ProvedorQuedaAnimada, useQuedaAnimadaTabletop } from './apresentacao/tabletop/useQuedaAnimada'
import { PainelAvisos } from './apresentacao/tabletop/PainelAvisos'
import { PainelCordas } from './apresentacao/tabletop/PainelCordas'
import { CATALOGO as CATALOGO_TABLETOP, PainelTabletop, type SecaoPainelTabletop } from './apresentacao/PainelTabletop'
import { PainelMesaTabletop } from './apresentacao/tabletop/PainelMesaTabletop'
import { analisarTalabartes, avaliarQueda, cenaComFlexaoDeEscadas, LIMITE_FORCA_CORPO_PADRAO_KN, avisosDaCena, buracosDoTerreno, definicaoDaFuncao, type VetorTabletop } from './dominio/tabletop'
import { BarraAcaoRapidaSelecao } from './apresentacao/tabletop/BarraAcaoRapidaSelecao'
import { BarraCordaSelecionada, BarraDesenhoCorda, BarraNoNaPonta, BarraPontoAncoragem, resumoDaAncoragemNoPonto } from './apresentacao/tabletop/BarrasCorda'
import { estadoNavegacao } from './visualizacao3d/camera/estadoNavegacao'
import { BarraTesteSistema } from './apresentacao/tabletop/BarraTesteSistema'
import { fitaPodeVirarV } from './dominio/tabletop/prenderNoPonto'
import { pontasQuePedemNo } from './dominio/tabletop/nosNasPontas'
import { BannerQuedaSolta } from './apresentacao/tabletop/BannerQuedaSolta'
import type { CenaTabletop } from './dominio/tabletop/tipos'
import { MedidasRapidas } from './apresentacao/tabletop/MedidasRapidas'
import { usePresetsDoInstrutor } from './apresentacao/tabletop/usePresetsDoInstrutor'
import { useTabletop } from './apresentacao/tabletop/useTabletop'
import { BarraFerramentasCenario } from './apresentacao/ribbon/BarraFerramentasCenario'
import {
  IconePonto,
  IconeSelecao,
} from './apresentacao/icones'
import { BarraAplicacao } from './apresentacao/workspace/BarraAplicacao'
import { BarraEstado, type ItemEstado } from './apresentacao/workspace/BarraEstado'
import { Workspace } from './apresentacao/workspace/Workspace'
import { moduloPorId, type IdModulo } from './apresentacao/workspace/modulos'
import { DockMobile } from './apresentacao/workspace/DockMobile'
import { DockCamera3D } from './apresentacao/workspace/DockCamera3D'
import { InspetorCAD } from './apresentacao/workspace/InspetorCAD'
import { TrilhaFerramentas } from './apresentacao/workspace/TrilhaFerramentas'
import { ferramentasTabletop } from './apresentacao/modulos/tabletopTools'
import { useModoLayout } from './apresentacao/workspace/useModoLayout'
import { useControleCamera } from './visualizacao3d/camera/comandosCamera'
import { PRESETS } from './apresentacao/presetsModo'
import { Visualizador3D } from './visualizacao3d/Visualizador3D'
import type { ValorMaquete } from './visualizacao3d/componentes/contextoMaquete'

/** mm:ss do tempo simulado, para o aviso de sessão salva. */
function formatarTempoAula(segundos: number): string {
  return `${Math.floor(segundos / 60).toString().padStart(2, '0')}:${(segundos % 60).toString().padStart(2, '0')}`
}

/** Só existe um módulo por enquanto; a tabela em `workspace/modulos` recebe os próximos. */
const MODULO_ATUAL: IdModulo = 'tabletop'
const MODULO = moduloPorId(MODULO_ATUAL)

/**
 * Contexto de peças da cena de fundo. Cada veículo do table top monta o próprio
 * contexto (ver `CenaTabletop`), então este só precisa existir e ser neutro.
 */
const CONTEXTO_CENA: ValorMaquete = {
  perfil: perfilVeiculoPorId('hatchCompacto'),
  modo: 'tabletop',
  camadas: PRESETS.tabletop.camadas,
  opacidadeCarroceria: PRESETS.tabletop.opacidadeCarroceria,
  estados: {},
  selecionada: null,
  selecionar: () => {},
  mostrarRotulos: PRESETS.tabletop.mostrarRotulos,
  expansaoAirbag: 0,
  srsAcionados: [],
  retencaoEmEvidencia: false,
  idsEmAlerta: [],
  colunasSeccionadas: [],
  pinturaPorAco: false,
  semCintoAncoras: [],
}

export default function App() {
  const [tema, setTema] = useState<'claro' | 'escuro'>(() => {
    if (typeof window === 'undefined') return 'escuro'
    return window.localStorage.getItem('csv-lab-tema') === 'claro' ? 'claro' : 'escuro'
  })
  const modoLayout = useModoLayout()
  const camera = useControleCamera()
  /* O inspetor abre sozinho quando algo é selecionado. Recolher é escolha do
     usuário e vale só até a seleção mudar — senão ele "some" e o aluno não
     entende por quê. */
  const [inspetorRecolhido, setInspetorRecolhido] = useState(false)
  const [grupoRibbonAtivo, setGrupoRibbonAtivo] = useState<string | null>(null)
  const [abaInspetorCAD, setAbaInspetorCAD] = useState<'operacao' | 'propriedades' | 'checklist'>('operacao')

  const tabletop = useTabletop({ mesaCompartilhada: true })
  const presetsDoInstrutor = usePresetsDoInstrutor()
  const [testeSistemaAtivo, setTesteSistemaAtivo] = useState(false)
  const [cenaSimuladaTeste, setCenaSimuladaTeste] = useState<CenaTabletop | null>(null)
  // cenários de exemplo (salvamento terrestre e em altura): são grandes, então sempre enquadra a cena inteira
  const carregarCenarioDeExemplo = useCallback((id?: string) => {
    tabletop.cancelarPosicionamento()
    setTesteSistemaAtivo(false)
    setCenaSimuladaTeste(null)
    tabletop.carregarExemplo(id)
    camera.executar('enquadrarCena')
  }, [tabletop, camera])
  useEffect(() => {
    tabletop.cancelarPosicionamento()
  }, [tabletop.modo])
  // o cenário padrão é grande (barranco de 4 m): ao abrir, a câmera enquadra a cena inteira.
  // O comando só vale com o canvas e os controles prontos, então espera um instante.
  useEffect(() => {
    const espera = window.setTimeout(() => camera.executar('enquadrarCena'), 600)
    return () => window.clearTimeout(espera)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [encaixeTabletopAtivo, setEncaixeTabletopAtivo] = useState(false)
  const [pontosAncoragemVisiveis, setPontosAncoragemVisiveis] = useState(false)
  const [setasTracaoVisiveis, setSetasTracaoVisiveis] = useState(true)
  const [destaqueEquipamentos, setDestaqueEquipamentos] = useState(false)
  const [animarPuxada, setAnimarPuxada] = useState(false)
  const [gradeVisivel, setGradeVisivel] = useState(true)
  const [pincelTabletopAtivo, setPincelTabletopAtivo] = useState(false)
  const [larguraPincelTabletop, setLarguraPincelTabletop] = useState(0.65)
  const [intrusaoPincelTabletop, setIntrusaoPincelTabletop] = useState(0.22)

  // Redefinir tudo: a cena volta ao cenário padrão e tudo que a rodeava volta ao que era na primeira abertura
  const redefinirTudo = useCallback(() => {
    tabletop.cancelarPosicionamento()
    tabletop.cancelarCorda()
    tabletop.cancelarAncoragem()
    tabletop.selecionarCorda(null)
    tabletop.selecionarAncoragem(null)
    setTesteSistemaAtivo(false)
    setCenaSimuladaTeste(null)
    tabletop.redefinirTudo()
    setEncaixeTabletopAtivo(false)
    setPontosAncoragemVisiveis(false)
    setSetasTracaoVisiveis(true)
    setDestaqueEquipamentos(false)
    setAnimarPuxada(false)
    setGradeVisivel(true)
    setPincelTabletopAtivo(false)
    setLarguraPincelTabletop(0.65)
    setIntrusaoPincelTabletop(0.22)
    setGrupoRibbonAtivo(null)
    setAbaInspetorCAD('operacao')
    camera.executar('enquadrarCena')
  }, [tabletop, camera])
  useEffect(() => {
    window.localStorage.setItem('csv-lab-tema', tema)
  }, [tema])

  /* Desfazer e refazer por teclado. Ignorado com o foco num campo, para não
     atrapalhar quem está digitando um número no painel. */
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      const alvo = evento.target as HTMLElement | null
      if (alvo?.tagName === 'INPUT' || alvo?.tagName === 'TEXTAREA' || alvo?.isContentEditable) return
      if (!(evento.ctrlKey || evento.metaKey) || evento.key.toLowerCase() !== 'z') return

      evento.preventDefault()
      if (evento.shiftKey) tabletop.refazer()
      else tabletop.desfazer()
    }

    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [tabletop.desfazer, tabletop.refazer])

  // Atalhos de manipulação do Table top: ignorados com o foco num campo de
  // texto, para não atrapalhar quem está digitando um nome no painel.
  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      const alvo = evento.target as HTMLElement | null
      const digitando = alvo?.tagName === 'INPUT' || alvo?.tagName === 'TEXTAREA' || alvo?.isContentEditable
      if (digitando) return

      const ctrlOuCmd = evento.ctrlKey || evento.metaKey
      if (ctrlOuCmd && evento.key.toLowerCase() === 'd') {
        if (tabletop.idSelecionado) {
          evento.preventDefault()
          tabletop.duplicar(tabletop.idSelecionado)
        }
      } else if (tabletop.desenhoAncoragem && (evento.key === 'Escape' || evento.key === 'Backspace' || evento.key === 'Delete')) {
        evento.preventDefault()
        if (evento.key === 'Escape') tabletop.cancelarAncoragem()
        else tabletop.removerUltimoPontoDoDesenhoAncoragem()
      } else if ((evento.key === 'Delete' || evento.key === 'Backspace') && tabletop.idAncoragemSelecionada) {
        evento.preventDefault()
        tabletop.removerAncoragem(tabletop.idAncoragemSelecionada)
      } else if (tabletop.desenhoCorda && (evento.key === 'Enter' || evento.key === 'Escape' || evento.key === 'Backspace' || evento.key === 'Delete')) {
        // com a corda em desenho, estas teclas são da corda e não do objeto ou da seleção
        evento.preventDefault()
        if (evento.key === 'Enter') tabletop.concluirCorda()
        else if (evento.key === 'Escape') tabletop.cancelarCorda()
        else tabletop.removerUltimoPontoDoDesenho()
      } else if ((evento.key === 'Delete' || evento.key === 'Backspace') && tabletop.idCordaSelecionada) {
        evento.preventDefault()
        tabletop.removerCorda(tabletop.idCordaSelecionada)
      } else if (evento.key === 'Delete' || evento.key === 'Backspace') {
        if (tabletop.idSelecionado) {
          evento.preventDefault()
          tabletop.remover(tabletop.idSelecionado)
        }
      } else if (evento.key === 'Escape') {
        if (tabletop.idOlhandoPara) { tabletop.cancelarOlharPara(); return }
        if (tabletop.tipoParaPosicionar) {
          tabletop.cancelarPosicionamento()
        } else {
          tabletop.selecionar(null)
          tabletop.selecionarCorda(null)
          tabletop.selecionarAncoragem(null)
        }
      } else if ((evento.key === 'w' || evento.key === 'W') && !estadoNavegacao.botaoDireito) {
        // com o botão direito apertado, W anda pela cena
        tabletop.escolherFerramenta('translate')
      } else if ((evento.key === 'e' || evento.key === 'E') && !estadoNavegacao.botaoDireito) {
        tabletop.escolherFerramenta('rotate')
      } else if (evento.key === 'r' || evento.key === 'R') {
        tabletop.escolherFerramenta('scale')
      }
    }

    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [
    tabletop.idSelecionado,
    tabletop.duplicar,
    tabletop.remover,
    tabletop.selecionar,
    tabletop.escolherFerramenta,
    tabletop.cancelarOlharPara,
    tabletop.idOlhandoPara,
    tabletop.tipoParaPosicionar,
    tabletop.cancelarPosicionamento,
    tabletop.desenhoCorda,
    tabletop.concluirCorda,
    tabletop.cancelarCorda,
    tabletop.removerUltimoPontoDoDesenho,
    tabletop.idCordaSelecionada,
    tabletop.removerCorda,
    tabletop.selecionarCorda,
    tabletop.desenhoAncoragem,
    tabletop.cancelarAncoragem,
    tabletop.removerUltimoPontoDoDesenhoAncoragem,
    tabletop.idAncoragemSelecionada,
    tabletop.removerAncoragem,
    tabletop.selecionarAncoragem,
  ])

  useEffect(() => {
    const aoTeclarGlobal = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault()
        setInspetorRecolhido((v) => !v)
      }
    }
    window.addEventListener('keydown', aoTeclarGlobal)
    return () => window.removeEventListener('keydown', aoTeclarGlobal)
  }, [])

  /* Peça pequena (mosquetão, polia, placa...) escolhida de longe: a câmera chega
     perto dela. De perto, nada muda. */
  const tipoSelecionado = tabletop.objetoSelecionado?.tipo
  useEffect(() => {
    if (tabletop.idSelecionado && tipoSelecionado && TIPOS_EM_DESTAQUE.has(tipoSelecionado)) {
      camera.executar('aproximarSeLonge', tabletop.idSelecionado)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabletop.idSelecionado])

  /* Uma seleção nova reabre o inspetor: recolher vale só até a seleção mudar. */
  useEffect(() => {
    setInspetorRecolhido(false)
  }, [tabletop.idSelecionado])

  // os avisos são recalculados só quando a cena muda
  const simulacoes = useSimulacoesTabletop(tabletop.cena)
  const quedaAnimada = useQuedaAnimadaTabletop(tabletop.cena)
  const avisosDaMontagem = useMemo(() => avisosDaCena(tabletop.cena), [tabletop.cena])
  const talabartes = useMemo(() => analisarTalabartes(tabletop.cena), [tabletop.cena])

  // Trabalhador arrastado para fora de uma borda e solto no ar: cai dali, preso (se o talabarte
  // alcança a ancoragem) ou em queda livre, e o resultado aparece com o nível da queda.
  const [soltoNoAr, setSoltoNoAr] = useState<string | null>(null)
  const [quedaDoSoltar, setQuedaDoSoltar] = useState<string | null>(null)
  // limite de força no corpo escolhido pelo instrutor; lembrado entre sessões
  const [limiteForcaKN, setLimiteForcaKN] = useState<number>(() => {
    try {
      const v = Number(window.localStorage.getItem('bizu.limiteForcaKN'))
      return Number.isFinite(v) && v > 0 ? v : LIMITE_FORCA_CORPO_PADRAO_KN
    } catch {
      return LIMITE_FORCA_CORPO_PADRAO_KN
    }
  })
  const mudarLimiteForcaKN = useCallback((v: number) => {
    setLimiteForcaKN(v)
    try { window.localStorage.setItem('bizu.limiteForcaKN', String(v)) } catch { /* sem armazenamento: vale só nesta sessão */ }
  }, [])
  const soltarTrabalhadorNoAr = useCallback(
    (id: string, posicao: VetorTabletop) => {
      const trabalhador = tabletop.cena.objetos.find((o) => o.id === id)
      if (!trabalhador) return
      tabletop.transformar(id, { ...trabalhador.transformacao, posicao })
      setSoltoNoAr(id)
    },
    [tabletop.cena, tabletop.transformar],
  )
  useEffect(() => {
    if (!soltoNoAr) return
    const id = soltoNoAr
    setSoltoNoAr(null)
    if (!tabletop.cena.objetos.some((o) => o.id === id)) return
    setQuedaDoSoltar(id)
    const talabarte = talabartes.find((t) => t.idTrabalhador === id && t.resultado.alcanca)
    if (talabarte) quedaAnimada.simular(talabarte.idCorda)
    else void quedaAnimada.simularQuedaLivre(id)
  }, [soltoNoAr, tabletop.cena, talabartes, quedaAnimada])
  useEffect(() => {
    if (quedaDoSoltar && !quedaAnimada.resultado && !soltoNoAr && quedaAnimada.estadoMotor !== 'carregando') {
      // a cena mudou (a queda some junto) ou a simulação não gerou nada
      setQuedaDoSoltar(null)
    }
  }, [quedaDoSoltar, quedaAnimada.resultado, quedaAnimada.estadoMotor, soltoNoAr])
  const resultadoDaQuedaSolta = quedaDoSoltar && quedaAnimada.resultado?.idTrabalhador === quedaDoSoltar ? quedaAnimada.resultado : null
  const quedaSoltaTerminou =
    resultadoDaQuedaSolta !== null && !quedaAnimada.tocando && quedaAnimada.indice >= resultadoDaQuedaSolta.quadros.length - 1
  const avaliacaoDaQueda = useMemo(
    () => (resultadoDaQuedaSolta && quedaSoltaTerminou ? avaliarQueda(tabletop.cena, resultadoDaQuedaSolta, { limiteForcaKN }) : null),
    [resultadoDaQuedaSolta, quedaSoltaTerminou, tabletop.cena, limiteForcaKN],
  )
  // a grade-régua fica no nível 0: num canal ou numa cisterna abertos ela atravessaria o vão, e no mato ela some sob o chão
  const gradeOcultaPelaCena = useMemo(
    () => tabletop.cena.chao === 'mato' || tabletop.cena.objetos.some((o) => buracosDoTerreno(o).length > 0),
    [tabletop.cena],
  )

  const renderPainelTabletop = (secoes: readonly SecaoPainelTabletop[]) => (
    <PainelTabletop
      secoes={secoes}
      montagemSimples={tabletop.modo === 'montar'}
      cena={tabletop.cena}
      selecionado={tabletop.objetoSelecionado}
      ferramenta={tabletop.ferramenta}
      fila={tabletop.fila}
      podeDesfazer={tabletop.podeDesfazer}
      podeRefazer={tabletop.podeRefazer}
      cenarioTravado={tabletop.cenarioTravado}
      aoAlternarTravaCenario={tabletop.alternarTravaCenario}
      aoSelecionar={tabletop.selecionar}
      aoEscolherFerramenta={tabletop.escolherFerramenta}
      aoDesvincularVitima={tabletop.desvincularVitima}
      aoAdicionar={tabletop.adicionar}
      aoIniciarPosicionamento={(tipo) => {
        tabletop.cancelarCorda()
        tabletop.cancelarAncoragem()
        tabletop.iniciarPosicionamento(tipo)
        // fecha a folha/dock pra equipe ver e tocar a cena 3D
        setGrupoRibbonAtivo(null)
      }}
      aoIniciarCorda={(funcao) => {
        tabletop.cancelarAncoragem()
        tabletop.iniciarCorda(funcao)
        // fecha a folha/dock pra equipe ver e tocar os pontos na cena 3D
        setGrupoRibbonAtivo(null)
      }}
      aoTransformar={tabletop.transformar}
      aoRenomearObjeto={tabletop.renomearObjeto}
      aoAlterarCor={tabletop.alterarCor}
      aoDuplicar={tabletop.duplicar}
      aoRemover={tabletop.remover}
      aoResetar={tabletop.resetarTransformacao}
      aoResetarPosicaoSoltura={tabletop.resetarPosicaoSoltura}
      aoRenomearCena={tabletop.renomearCena}
      aoCriarCena={tabletop.criarCena}
      aoRedefinirTudo={redefinirTudo}
      aoCarregarExemplo={carregarCenarioDeExemplo}
      aoImportar={tabletop.importar}
      presetsDoInstrutor={{
        lista: presetsDoInstrutor.presets,
        aoSalvar: (nome) => presetsDoInstrutor.salvar(tabletop.cena, nome),
        aoAbrir: (id) => {
          const cena = presetsDoInstrutor.abrir(id)
          if (!cena) return false
          tabletop.abrirCenaMontada(cena)
          camera.executar('enquadrarCena')
          return true
        },
        aoExcluir: (id) => presetsDoInstrutor.excluir(id),
      }}
      aoDesfazer={tabletop.desfazer}
      aoRefazer={tabletop.refazer}
      aoMoverNaFila={tabletop.moverNaFila}
      ancorasLivres={tabletop.ancorasLivres}
      aoAtualizarVitima={tabletop.atualizarVitima}
      aoAtualizarRisco={tabletop.atualizarRisco}
      aoAtualizarParametros={tabletop.atualizarParametros}
      aoEnvolverComFita={tabletop.envolverComFita}
      aoAlinharPolia={tabletop.alinharPolia}
      aoAlterarEdicaoContinua={tabletop.aoAlterarManipulacao}
      aoAtualizarVeiculo={tabletop.atualizarVeiculo}
      aoDefinirPosicaoFinal={tabletop.definirPosicaoFinal}
      aoExecutarManejoVidro={tabletop.executarManejoVidro}
      aoRestaurarVidros={tabletop.restaurarVidros}
      ultimoResultadoVidro={tabletop.ultimoResultadoVidro}
      aoAplicarDeformacao={tabletop.aplicarDeformacaoVeiculo}
      pincel={{
        ativo: pincelTabletopAtivo,
        larguraM: larguraPincelTabletop,
        intrusaoM: intrusaoPincelTabletop,
        aoAlternar: (ativo) => {
          if (ativo) {
            tabletop.cancelarPosicionamento()
            setGrupoRibbonAtivo(null)
          }
          setPincelTabletopAtivo(ativo)
        },
        aoDefinirLargura: setLarguraPincelTabletop,
        aoDefinirIntrusao: setIntrusaoPincelTabletop,
        aoRestaurar: tabletop.limparAmassados,
      }}
      aoAlternarBloqueio={tabletop.alternarBloqueio}
      aoAlternarOculto={tabletop.alternarOculto}
    />
  )

  const renderRoteiroAula = () => (
    <PainelMesaTabletop
      cena={tabletop.cena}
      selecionado={tabletop.objetoSelecionado}
      aoSelecionar={tabletop.selecionar}
      aoAlterarHistoria={tabletop.alterarHistoria}
      aoCarregarCenario={carregarCenarioDeExemplo}
    />
  )

  // corda escolhida na cena: o inspetor abre nas propriedades dela
  useEffect(() => {
    if (!tabletop.idCordaSelecionada) return
    setInspetorRecolhido(false)
    setAbaInspetorCAD('propriedades')
  }, [tabletop.idCordaSelecionada])

  const inspetorCorda =
    tabletop.modo === 'montar' && tabletop.cordaSelecionada && !tabletop.objetoSelecionado
      ? {
          titulo: tabletop.cordaSelecionada.rotulo ?? definicaoDaFuncao(tabletop.cordaSelecionada.funcao).rotulo,
          subtitulo: 'corda',
          acoes: (
        <BarraCordaSelecionada
          noInspetor
          cena={tabletop.cena}
          corda={tabletop.cordaSelecionada}
          aoEscolherFuncao={(funcao) => {
            const corda = tabletop.cordaSelecionada!
            // a função que nasce com seta (a tração) ganha a seta se a corda ainda não tinha
            const comSeta = definicaoDaFuncao(funcao).nascemComSeta && !corda.sentido
            tabletop.atualizarCorda(corda.id, { funcao, ...(comSeta ? { sentido: 'paraFim' as const } : {}) })
          }}
          aoInverterSentido={() => tabletop.inverterSentido(tabletop.cordaSelecionada!.id)}
          aoMoverPonta={tabletop.iniciarMoverPonta}
          aoMosquetaoNoV={fitaPodeVirarV(tabletop.cena, tabletop.cordaSelecionada.id) ? () => tabletop.mosquetaoNoVDaFita(tabletop.cordaSelecionada!.id) : undefined}
          aoAmarrarPontas={
            pontasQuePedemNo(tabletop.cena, tabletop.cordaSelecionada.id).length > 0
              ? () => tabletop.pedirNosDasPontas(tabletop.cordaSelecionada!.id)
              : undefined
          }
          aoRemover={() => tabletop.removerCorda(tabletop.cordaSelecionada!.id)}
          aoFechar={() => tabletop.selecionarCorda(null)}
        />
          ),
          aoFechar: () => tabletop.selecionarCorda(null),
        }
      : null

  const ferramentas = ferramentasTabletop({
    inspetorCorda,
    painelRoteiroAula: renderRoteiroAula,
    secaoAtiva: 'cenario',
    aoAtivarSecao: () => {
      tabletop.cancelarPosicionamento()
      setInspetorRecolhido(false)
      setGrupoRibbonAtivo(null)
      setAbaInspetorCAD('operacao')
    },
    modoRoteiroAula: true,
    asfaltoAtivo: tabletop.asfaltoAtivo,
    aoAlternarAsfalto: tabletop.alternarAsfalto,
    cenarioTravado: tabletop.cenarioTravado,
    aoAlternarTravaCenario: tabletop.alternarTravaCenario,
    encaixeAtivo: encaixeTabletopAtivo,
    aoAlternarEncaixe: () => setEncaixeTabletopAtivo((ativo) => !ativo),
    setasTracaoAtivas: setasTracaoVisiveis,
    gradeAtiva: gradeVisivel,
    gradeOcultaPelaCena,
    aoAlternarGrade: () => setGradeVisivel((v) => !v),
    aoAlternarSetasTracao: () => setSetasTracaoVisiveis((ativo) => !ativo),
    destaqueEquipamentosAtivo: destaqueEquipamentos,
    aoAlternarDestaqueEquipamentos: () => setDestaqueEquipamentos((ativo) => !ativo),
    animarPuxadaAtivo: animarPuxada,
    aoAlternarAnimarPuxada: () => setAnimarPuxada((ativo) => !ativo),
    testeSistemaAtivo,
    aoTestarSistema: () => {
      setTesteSistemaAtivo((ativo) => {
        if (ativo) {
          setCenaSimuladaTeste(null)
          return false
        }
        return true
      })
    },
    pontosAncoragemAtivos: pontosAncoragemVisiveis,
    aoAlternarPontosAncoragem: () => setPontosAncoragemVisiveis((ativo) => !ativo),
    painelAdicionar: () => renderPainelTabletop(['adicionar']),
    painelCordas: () => (
      <>
      <PainelCordas
        cena={tabletop.cena}
        desenho={tabletop.desenhoCorda}
        cordaSelecionada={tabletop.cordaSelecionada}
        aoIniciar={(funcao) => {
          tabletop.iniciarCorda(funcao)
          // fecha a folha/dock pra equipe ver e tocar os pontos na cena 3D
          setGrupoRibbonAtivo(null)
        }}
        aoTrocarFuncaoDoDesenho={tabletop.trocarFuncaoDoDesenho}
        aoDesfazerPonto={tabletop.removerUltimoPontoDoDesenho}
        aoConcluir={tabletop.concluirCorda}
        aoCancelar={tabletop.cancelarCorda}
        aoSelecionarCorda={tabletop.selecionarCorda}
        aoAtualizarCorda={tabletop.atualizarCorda}
        aoInverterSentido={tabletop.inverterSentido}
        aoDefinirFolga={tabletop.definirFolga}
        aoRemoverCorda={tabletop.removerCorda}
      />
      <PainelAncoragens
        cena={tabletop.cena}
        desenho={tabletop.desenhoAncoragem}
        ancoragemSelecionada={tabletop.ancoragemSelecionada}
        aoIniciar={(tipo) => {
          tabletop.iniciarAncoragem(tipo)
          setGrupoRibbonAtivo(null)
        }}
        aoDesfazerPonto={tabletop.removerUltimoPontoDoDesenhoAncoragem}
        aoCancelar={tabletop.cancelarAncoragem}
        aoSelecionar={tabletop.selecionarAncoragem}
        aoAtualizar={tabletop.atualizarAncoragem}
        aoRemover={tabletop.removerAncoragem}
      />
      </>
    ),
    quantidadeCordas: (tabletop.cena.cordas?.length ?? 0) + (tabletop.cena.ancoragens?.length ?? 0),
    painelAvisos: () => (
      <PainelAvisos
        cena={tabletop.cena}
        aoIrPara={(aviso) => {
          if (aviso.idCorda) tabletop.selecionarCorda(aviso.idCorda)
          else if (aviso.idAncoragem) tabletop.selecionarAncoragem(aviso.idAncoragem)
          else if (aviso.idObjeto) tabletop.selecionar(aviso.idObjeto)
          setGrupoRibbonAtivo(null)
        }}
      />
    ),
    quantidadeAvisos: avisosDaMontagem.length,
    painelFerramentas: () => renderPainelTabletop(['ferramentas']),
    painelFila: () => renderPainelTabletop(['fila']),
    painelObjetos: () => renderPainelTabletop(['objetos']),
    painelCena: () => renderPainelTabletop(['cena']),
    painelIo: () => renderPainelTabletop(['io']),
    painelEditor: () => renderPainelTabletop(['editor']),
    quantidadeObjetos: tabletop.cena.objetos.length,
    quantidadeFila: tabletop.fila.length,
    ferramenta: tabletop.ferramenta,
    aoEscolherFerramenta: tabletop.escolherFerramenta,
    desfazer: tabletop.desfazer,
    refazer: tabletop.refazer,
    podeDesfazer: tabletop.podeDesfazer,
    podeRefazer: tabletop.podeRefazer,
    objetoSelecionado: tabletop.objetoSelecionado
      ? {
          id: tabletop.objetoSelecionado.id,
          nome: tabletop.objetoSelecionado.nome,
          tipo: tabletop.objetoSelecionado.tipo,
          posicao: tabletop.objetoSelecionado.transformacao.posicao,
          rotacaoGraus: Math.round(
            (tabletop.objetoSelecionado.transformacao.rotacao[1] * 180) / Math.PI,
          ),
          escala: tabletop.objetoSelecionado.transformacao.escala[0],
          oculto: tabletop.objetoSelecionado.oculto ?? false,
          bloqueado: tabletop.objetoSelecionado.bloqueado ?? false,
          aoDuplicar: () => tabletop.duplicar(tabletop.objetoSelecionado!.id),
          aoRestaurar: () => tabletop.resetarTransformacao(tabletop.objetoSelecionado!.id),
          aoRemover: () => tabletop.remover(tabletop.objetoSelecionado!.id),
        }
      : null,
    aoLimparSelecao: () => tabletop.selecionar(null),
  })

  /* Estado de relance, no rodapé: módulo e o que está selecionado. Cada item
     leva ícone além da cor. */
  const nomeSelecionado = tabletop.objetoSelecionado?.nome
  const IconeDoModulo = MODULO.Icone
  const itensEstado: ItemEstado[] = [
    {
      id: 'modulo',
      rotulo: 'Módulo',
      valor: MODULO.titulo,
      icone: <IconeDoModulo />,
      tom: 'info',
    },
    {
      id: 'selecao',
      rotulo: 'Seleção',
      valor: nomeSelecionado ?? 'nada selecionado',
      icone: nomeSelecionado ? <IconeSelecao /> : <IconePonto />,
    },
  ]

  /* Só o CENÁRIO, sem o nome do módulo: ele já aparece aceso na navegação e por
     extenso na barra de estado, e repeti-lo aqui custava a largura que os grupos
     e as ações rápidas precisam. */
  const identificadorCenario = (
    <div className="cenario-identificador-compacto">
      <span className="cenario-identificador-compacto__sub">
        {`${tabletop.cena.nome} • ${tabletop.cena.objetos.length} objeto(s)`}
      </span>
    </div>
  )

  const idSelecionado = tabletop.idSelecionado
  // as escadas se curvam para o lado do peso (só no desenho: a cena em edição não muda)
  const cenaExibida = useMemo(() => cenaComFlexaoDeEscadas(cenaSimuladaTeste ?? tabletop.cena), [cenaSimuladaTeste, tabletop.cena])

  return (
    <ProvedorSimulacoes valor={simulacoes}>
    <ProvedorQuedaAnimada valor={quedaAnimada}>
    <Workspace
      tema={tema}
      modoLayout={modoLayout}
      barraAplicacao={
        <BarraAplicacao
          moduloAtual={MODULO_ATUAL}
          aoTrocarModulo={() => {}}
          tema={tema}
          aoAlternarTema={() => setTema((atual) => (atual === 'claro' ? 'escuro' : 'claro'))}
          modoLayout={modoLayout}
        />
      }
      ribbon={
        modoLayout === 'mobile' ? null : (
          <BarraFerramentasCenario
            titulo={`Ferramentas de ${MODULO.titulo}`}
            identificadorCenario={identificadorCenario}
            grupos={ferramentas.grupos}
            grupoAtivo={grupoRibbonAtivo}
            aoMudarGrupo={setGrupoRibbonAtivo}
            chips={ferramentas.chips}
            acoesRapidas={ferramentas.acoesRapidas}
            chaveContexto={MODULO_ATUAL}
          />
        )
      }
      dock={
        modoLayout === 'mobile' ? (
          <DockMobile
            grupos={ferramentas.grupos}
            grupoAtivo={grupoRibbonAtivo}
            aoMudarGrupo={setGrupoRibbonAtivo}
            tituloModulo={MODULO.titulo}
            acoesRapidas={ferramentas.acoesRapidas}
            acoesFixas={ferramentas.acoesFixasMobile}
            rotuloOpcoes={ferramentas.rotuloOpcoesMobile}
            inspetor={ferramentas.inspetor ?? null}
          />
        ) : undefined
      }
      canvas={
        <>
          <Visualizador3D
            comGrade={gradeVisivel && !gradeOcultaPelaCena}
            comandoCamera={camera.comando}
            contexto={CONTEXTO_CENA}
            tabletop={{
              objetos: cenaExibida.objetos,
              idSelecionado: tabletop.idSelecionado,
              ferramenta: tabletop.ferramenta,
              aoSelecionar: (id) => {
                if (testeSistemaAtivo) return
                tabletop.selecionarNaMesa(id)
                // tocar no vazio tira toda seleção, inclusive a da corda e a da ancoragem
                if (id === null) {
                  tabletop.selecionarCorda(null)
                  tabletop.selecionarAncoragem(null)
                }
              },
              cordas: cenaExibida.cordas,
              idCordaSelecionada: tabletop.idCordaSelecionada,
              aoSelecionarCorda: tabletop.selecionarCorda,
              aoReposicionarPontoCorda: tabletop.modo === 'montar' && !testeSistemaAtivo ? tabletop.reposicionarPontoDaCorda : undefined,
              desenhoCorda: tabletop.desenhoCorda,
              aoEscolherPontoCorda: (referencia) =>
                tabletop.movendoPonta
                  ? tabletop.moverPontaPara(referencia)
                  : tabletop.desenhoAncoragem
                    ? tabletop.escolherPontoDaAncoragem(referencia)
                    : tabletop.adicionarPontoAoDesenho(referencia),
              escolhendoPontoDeCorda: Boolean(tabletop.movendoPonta),
              // fora do desenho, tocar num ponto da peça selecionada abre as ações do ponto
              aoTocarPontoAncoragem: tabletop.modo === 'montar' && !testeSistemaAtivo ? tabletop.escolherPonto : undefined,
              aoPosicionarNoPonto: tabletop.modo === 'montar' && !testeSistemaAtivo ? tabletop.posicionarNoPonto : undefined,
              pontoEscolhido: tabletop.pontoEscolhido,
              ancoragens: cenaExibida.ancoragens,
              idAncoragemSelecionada: tabletop.idAncoragemSelecionada,
              // tocar na ancoragem já abre as ações do ponto central dela
              aoSelecionarAncoragem: tabletop.tocarAncoragem,
              desenhoAncoragem: tabletop.desenhoAncoragem,
              simulacaoAncoragem: simulacoes.quadroAtual,
              quedaAnimada: quedaAnimada.naCena,
              aoTransformar: tabletop.transformar,
              aoAlterarManipulacao: tabletop.aoAlterarManipulacao,
              aoCancelarManipulacao: tabletop.cancelarManipulacao,
              aoSoltarTrabalhadorNoAr: soltarTrabalhadorNoAr,
              encaixeAtivo: encaixeTabletopAtivo,
              exibirAsfalto: tabletop.asfaltoAtivo,
              chao: tabletop.cena.chao,
              cenarioTravado: tabletop.cenarioTravado,
              mostrarPontosAncoragem: pontosAncoragemVisiveis,
              mostrarSetasTracao: setasTracaoVisiveis,
              destaqueEquipamentos,
              animarPuxada,
              talabartes,
              arrastoNaSessao: true,
              pincel: {
                ativo: pincelTabletopAtivo && tabletop.modo === 'montar' && !tabletop.tipoParaPosicionar,
                larguraM: larguraPincelTabletop,
                intrusaoM: intrusaoPincelTabletop,
                aoAplicar: (idVeiculo, ponto) => tabletop.aplicarAmassadoNoPonto(
                  idVeiculo, ponto, larguraPincelTabletop, intrusaoPincelTabletop,
                ),
              },
              aoSelecionarPeca: tabletop.selecionarPecaVeiculo,
              tipoParaPosicionar: tabletop.tipoParaPosicionar,
              escolhendoAlvo: tabletop.idOlhandoPara !== null,
              aoPosicionarNovaPeca: (tipo, x, z, y, rotacao) => tabletop.adicionarNaPosicao(tipo, x, z, encaixeTabletopAtivo, y, rotacao),
              pedidoTesteBalanco: tabletop.pedidoTesteBalanco,
            }}
          />

          <div className="badge-prototipo">
            {`Table top livre • ${tabletop.cena.objetos.length} objeto(s)`}
          </div>
          {testeSistemaAtivo ? (
            <BarraTesteSistema
              cena={tabletop.cena}
              aoAtualizarCenaSimulada={setCenaSimuladaTeste}
              aoFechar={() => {
                setTesteSistemaAtivo(false)
                setCenaSimuladaTeste(null)
              }}
            />
          ) : avaliacaoDaQueda && resultadoDaQuedaSolta ? (
            <BannerQuedaSolta
              avaliacao={avaliacaoDaQueda}
              aoRepetir={quedaAnimada.alternarReproducao}
              aoMudarLimiteKN={mudarLimiteForcaKN}
              aoConcluir={() => {
                const ultimo = resultadoDaQuedaSolta.quadros[resultadoDaQuedaSolta.quadros.length - 1]
                const trabalhador = tabletop.cena.objetos.find((o) => o.id === resultadoDaQuedaSolta.idTrabalhador)
                if (ultimo && trabalhador) {
                  tabletop.transformar(trabalhador.id, { ...trabalhador.transformacao, posicao: ultimo.pes })
                }
                setQuedaDoSoltar(null)
              }}
            />
          ) : tabletop.modo === 'montar' && tabletop.desenhoCorda ? (
            <BarraDesenhoCorda
              cena={tabletop.cena}
              funcao={tabletop.desenhoCorda.funcao}
              percurso={tabletop.desenhoCorda.percurso}
              aoDesfazerPonto={tabletop.removerUltimoPontoDoDesenho}
              aoConcluir={tabletop.concluirCorda}
              aoCancelar={tabletop.cancelarCorda}
            />
          ) : tabletop.modo === 'montar' && tabletop.movendoPonta ? (
            <div className="tt-banner-posicionamento tt-barra-corda" role="group" aria-label="Movendo a ponta da corda">
              <span className="tt-barra-corda__texto" aria-live="polite">
                Toque no novo ponto para o {tabletop.movendoPonta.lado === 'inicio' ? 'início' : 'fim'} da corda.
              </span>
              <button type="button" onClick={tabletop.cancelarMoverPonta}>
                Cancelar
              </button>
            </div>
          ) : tabletop.modo === 'montar' && tabletop.pontaParaAmarrar ? (
            <BarraNoNaPonta
              cena={tabletop.cena}
              idCorda={tabletop.pontaParaAmarrar.idCorda}
              lado={tabletop.pontaParaAmarrar.lado}
              aoEscolher={tabletop.responderNoDaPonta}
              aoFechar={tabletop.cancelarNosDasPontas}
            />
          ) : tabletop.modo === 'montar' && tabletop.fitaParaJuntar ? (
            <div className="tt-banner-posicionamento tt-barra-corda" role="alert">
              <span>Toque na ponta da outra fita para juntá-las num ponto de ancoragem diretor em V.</span>
              <button type="button" onClick={tabletop.cancelarJuntarFitas}>Cancelar</button>
            </div>
          ) : tabletop.modo === 'montar' && tabletop.pontoEscolhido ? (
            <BarraPontoAncoragem
              cena={tabletop.cena}
              ponto={tabletop.pontoEscolhido}
              resumo={resumoDaAncoragemNoPonto(tabletop.cena, tabletop.pontoEscolhido)}
              aoPrenderCorda={() => tabletop.prenderCordaNoPonto('livre')}
              aoPrenderFita={() => tabletop.prenderCordaNoPonto('fita')}
              aoPrenderPeca={tabletop.prenderPecaNoPontoEscolhido}
              aoPrenderNoComCorda={tabletop.prenderNoComCordaNoPontoEscolhido}
              aoJuntarComOutraFita={() => tabletop.iniciarJuntarFita(tabletop.pontoEscolhido!)}
              aoFechar={() => tabletop.escolherPonto(null)}
            />
          ) : tabletop.idOlhandoPara ? (
            <div className="tt-banner-posicionamento">
              Clique em um carro ou vítima para virar o socorrista para esse ponto.
              <button type="button" onClick={tabletop.cancelarOlharPara}>Cancelar</button>
            </div>
          ) : tabletop.tipoParaPosicionar ? (
            <div className="tt-banner-posicionamento">
              Toque no chão — ou num ponto de ancoragem, para prender ali — para posicionar {CATALOGO_TABLETOP.find((i) => i.tipo === tabletop.tipoParaPosicionar)?.rotulo ?? 'a peça'}
              <button type="button" onClick={tabletop.cancelarPosicionamento}>
                Cancelar
              </button>
            </div>
          ) : tabletop.modo === 'montar' && tabletop.objetoSelecionado ? (
            <BarraAcaoRapidaSelecao
              objeto={tabletop.objetoSelecionado}
              onGirar90={() => tabletop.girar(tabletop.objetoSelecionado!.id, 90)}
              onGirar={(delta) => tabletop.girar(tabletop.objetoSelecionado!.id, delta)}
              onDefinirDirecao={(graus) => tabletop.definirDirecao(tabletop.objetoSelecionado!.id, graus)}
              onIniciarDirecao={() => tabletop.aoAlterarManipulacao(true)}
              onFinalizarDirecao={() => tabletop.aoAlterarManipulacao(false)}
              onOlharPara={() => tabletop.iniciarOlharPara(tabletop.objetoSelecionado!.id)}
              bloqueioCenario={tabletop.cenarioTravado}
              onDuplicar={() => tabletop.duplicar(tabletop.objetoSelecionado!.id)}
              onAlternarBloqueio={() =>
                tabletop.alternarBloqueio(tabletop.objetoSelecionado!.id, !tabletop.objetoSelecionado!.bloqueado)
              }
              onAlternarOculto={() => {
                const id = tabletop.objetoSelecionado!.id
                tabletop.alternarOculto(id, !tabletop.objetoSelecionado!.oculto)
                // uma peça oculta não tem mais o que essa barra flutuante apontar
                tabletop.selecionar(null)
              }}
              onRemover={() => tabletop.remover(tabletop.objetoSelecionado!.id)}
              onAbrirEditor={() => setGrupoRibbonAtivo('editor')}
              medidas={
                <MedidasRapidas
                  objeto={tabletop.objetoSelecionado}
                  aoAtualizar={tabletop.atualizarParametros}
                  aoAlterarEdicaoContinua={tabletop.aoAlterarManipulacao}
                />
              }
              onTestarEstabilidade={
                tabletop.objetoSelecionado.veiculo?.posicaoFinal === 'sobreRodas'
                  ? () => tabletop.testarBalanco(tabletop.objetoSelecionado!.id)
                  : undefined
              }
            />
          ) : (
            <div className="ajuda-camera">
              Toque para selecionar • segure e arraste para mover • arraste o chão para girar a cena
            </div>
          )}
        </>
      }
      trilha={
        modoLayout === 'mobile' ? undefined : (
          <TrilhaFerramentas
            camera={{
              executar: (tipo) => camera.executar(tipo, idSelecionado),
              vistaAtiva: camera.vistaAtiva,
            }}
            temSelecao={idSelecionado != null}
            historico={{
              desfazer: tabletop.desfazer,
              refazer: tabletop.refazer,
              podeDesfazer: tabletop.podeDesfazer,
              podeRefazer: tabletop.podeRefazer,
            }}
            modoLayout={modoLayout}
          />
        )
      }
      dockCamera={
        <DockCamera3D
          camera={camera}
          temSelecao={idSelecionado != null}
          modoLayout={modoLayout}
          comEnquadrarCena
        />
      }
      inspetor={
        modoLayout === 'mobile' ? undefined : (
          <InspetorCAD
            tituloModulo={MODULO.titulo}
            modoLayout={modoLayout}
            recolhido={inspetorRecolhido}
            aoAlternarRecolhido={() => setInspetorRecolhido((v) => !v)}
            abaAtiva={abaInspetorCAD}
            aoTrocarAba={setAbaInspetorCAD}
            somenteOperacao
            conteudoPropriedades={ferramentas.inspetor ?? null}
            conteudoOperacao={renderRoteiroAula()}
            aoLimparSelecao={() => tabletop.selecionar(null)}
          />
        )
      }
      barraEstado={<BarraEstado itens={itensEstado} />}
      rodape={
        <footer className="aviso">
          Apoio a treinamento. Não substitui doutrina, instrutor nem protocolo operacional.
          <span className="aviso__direitos">{' · '}© 2026 Marco Guerra. Todos os direitos reservados.</span>
          <span className="aviso__creditos">
            {' · '}Desenvolvido por{' '}
            <a
              href="https://marcoguerra.dev.br"
              target="_blank"
              rel="noreferrer"
              className="marca-digital-bricks"
            >
              <img
                src="/logo-digital-bricks.png"
                alt=""
                aria-hidden="true"
                className="marca-digital-bricks__logo"
              />
              <span>Marco Guerra</span>
            </a>
          </span>
        </footer>
      }
    />
    </ProvedorQuedaAnimada>
    </ProvedorSimulacoes>
  )
}
